import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { myApi } from "../api";
import { gaEcommerce, gaItemsFromCart } from "@/lib/ga";
import { useMyAuth } from "../contexts/MyAuthContext";

/**
 * /my/cart/add — landing route activated when WordPress/WooCommerce sends
 * an unauthenticated visitor through the unified `/login?next=…` flow.
 * After login, this page resumes the add-to-cart with the same product
 * the user originally clicked, then forwards them to the cart UI.
 *
 * URL params: ?id=<ID_Produto>&q=<Quantidade>&type=ADD|SET
 */
export default function MyCartAdd() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useMyAuth();
  const [error, setError] = useState("");
  // Garante que a adição (e o add_to_cart) corre uma só vez por visita, mesmo
  // que o efeito corra duas vezes (StrictMode) ou user/authLoading mudem a meio.
  // NÃO é reposta na limpeza do efeito: fica marcada durante toda a visita.
  const doneRef = useRef(null);

  const id = Number(params.get("id") || 0);
  const qty = Math.max(1, Number(params.get("q") || 1));
  const typeOfUpdate = params.get("type") || "ADD";

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      // Should not happen — MyLayout already enforces auth — but be defensive.
      const next = `/my/cart/add?id=${id}&q=${qty}&type=${typeOfUpdate}`;
      window.location.href = `/login?next=${encodeURIComponent(next)}`;
      return;
    }
    if (!id) {
      setError("Produto inválido.");
      return;
    }
    const key = `${id}:${qty}:${typeOfUpdate}`;
    if (doneRef.current === key) return;
    doneRef.current = key;
    (async () => {
      try {
        await myApi.cartAdd(id, qty, typeOfUpdate);
        // Evento add_to_cart com dados completos — lê o carrinho actualizado
        // para obter nome e preço (ajustado ao perfil). Não bloqueia a navegação.
        try {
          const cartResp = await myApi.cartGet();
          const items = gaItemsFromCart(cartResp, [{ id_produto: id, quantidade: qty }]);
          const value = items.reduce((s, it) => s + it.price * it.quantity, 0);
          gaEcommerce("add_to_cart", items, value);
        } catch (_ga) { /* analytics nunca bloqueia o fluxo */ }
        navigate("/my/cart", { replace: true });
      } catch (e) {
        setError(e?.response?.data?.message || "Não foi possível adicionar o produto ao carrinho.");
      }
    })();
  }, [authLoading, user, id, qty, typeOfUpdate, navigate]);

  return (
    <div data-testid="my-cart-add-loading" className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        {error ? (
          <div data-testid="my-cart-add-error" className="text-[#DC2626] text-sm max-w-md">
            <div className="font-semibold mb-1">Erro</div>
            <div>{error}</div>
            <button
              data-testid="my-cart-add-back"
              onClick={() => navigate("/my")}
              className="mt-4 text-[#0F1829] underline text-sm"
            >Voltar à área cliente</button>
          </div>
        ) : (
          <>
            <div className="w-10 h-10 border-2 border-[#0F1829]/20 border-t-[#0F1829] rounded-full animate-spin mx-auto mb-3" />
            <div className="text-sm text-[#6B7280]">A adicionar ao carrinho…</div>
          </>
        )}
      </div>
    </div>
  );
}
